import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE, publicCachedGet } from '../utils/api';
import { repositorySeo } from '../utils/seo';

function PluginShowcase() {
  const [allPlugins, setAllPlugins] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [newPlugins, setNewPlugins] = useState([]);
  const [popularPlugins, setPopularPlugins] = useState([]);
  const [topRatedPlugins, setTopRatedPlugins] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [workflowSearchResults, setWorkflowSearchResults] = useState([]);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [slugCounts, setSlugCounts] = useState({});
  const navigate = useNavigate();
  const isLoggedIn = Boolean(localStorage.getItem('token'));

  const categories = [
    'all', 'utility', 'productivity', 'development', 'database',
    'security', 'automation', 'api', 'analytics', 'workflows'
  ];

  useEffect(() => {
    repositorySeo();
    loadPlugins();
  }, []);

  const loadPlugins = async () => {
    setLoading(true);
    try {
      const [response, workflowResponse] = await Promise.all([
        publicCachedGet(`${API_BASE}/plugin/approved`, 60000),
        publicCachedGet(`${API_BASE}/workflow/approved`, 60000)
      ]);
      const loadedPlugins = Array.isArray(response.data) ? response.data : [];
      setWorkflows(Array.isArray(workflowResponse.data) ? workflowResponse.data : []);

      setAllPlugins(loadedPlugins);
      setPlugins(loadedPlugins);
      setSlugCounts(buildSlugCounts(loadedPlugins));

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      setNewPlugins(
        loadedPlugins
          .filter((p) => new Date(p.publishedAt) > thirtyDaysAgo)
          .slice(0, 6)
      );

      setPopularPlugins(
        [...loadedPlugins]
          .sort((a, b) => b.downloads - a.downloads)
          .slice(0, 6)
      );

      setTopRatedPlugins(
        [...loadedPlugins]
          .filter((p) => p.averageRating >= 4)
          .sort((a, b) => b.averageRating - a.averageRating)
          .slice(0, 6)
      );
    } catch (error) {
      console.error('Error loading plugins:', error);
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    const query = String(searchQuery || '').trim();
    if (!query) {
      setActiveSearchQuery('');
      setSearchResults([]);
      setWorkflowSearchResults([]);
      setSelectedCategory('all');
      await loadPlugins();
      return;
    }

    setSearching(true);
    try {
      const [response, workflowResponse] = await Promise.all([
        publicCachedGet(`${API_BASE}/plugin/search?q=${encodeURIComponent(query)}`, 30000),
        publicCachedGet(`${API_BASE}/workflow/search?q=${encodeURIComponent(query)}`, 30000)
      ]);
      const results = Array.isArray(response.data) ? response.data : [];
      setSearchResults(results);
      setWorkflowSearchResults(Array.isArray(workflowResponse.data) ? workflowResponse.data : []);
      setSlugCounts(buildSlugCounts(results));
      setActiveSearchQuery(query);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setWorkflowSearchResults([]);
      setActiveSearchQuery(query);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = async (event) => {
    event?.preventDefault?.();
    await handleSearch();
  };

  const filterByCategory = (category) => {
    setSelectedCategory(category);
    setActiveSearchQuery('');
    setSearchResults([]);
    setWorkflowSearchResults([]);
    const source = Array.isArray(allPlugins) ? allPlugins : [];
    const filtered = category === 'all'
      ? source
      : source.filter((p) => String(p.tags || '').toLowerCase().includes(category.toLowerCase()));
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setPlugins(filtered);
    setSlugCounts(buildSlugCounts(filtered));
    setNewPlugins(
      filtered
        .filter((p) => new Date(p.publishedAt) > thirtyDaysAgo)
        .slice(0, 6)
    );
    setPopularPlugins(
      [...filtered]
        .sort((a, b) => b.downloads - a.downloads)
        .slice(0, 6)
    );
    setTopRatedPlugins(
      [...filtered]
        .filter((p) => p.averageRating >= 4)
        .sort((a, b) => b.averageRating - a.averageRating)
        .slice(0, 6)
    );
  };

  const buildPluginUrl = (plugin) => {
    const slug = plugin.slug || toSlug(plugin.name);
    const needsId = slugCounts[slug] > 1;
    return needsId ? `/plugin/${slug}/${plugin.id}` : `/plugin/${slug}`;
  };

  const showingSearchResults = !!activeSearchQuery;
  const buildWorkflowUrl = (workflow) => `/workflow/${workflow.slug || toSlug(workflow.name)}/${workflow.id}`;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div className="repository-header-content" style={styles.headerContent}>
          <div className="repository-brand" style={styles.brandWrap}>
            <div style={styles.logoMark} aria-hidden="true">
              <span style={{ ...styles.logoDot, ...styles.logoDotLeft }} />
              <span style={{ ...styles.logoDot, ...styles.logoDotCenter }} />
              <span style={{ ...styles.logoDot, ...styles.logoDotRight }} />
              <span style={styles.logoMarkTail} />
            </div>
            <h1 className="repository-logo-text" style={styles.logo}>GotChat AI Plugin Repository</h1>
          </div>
          <form className="repository-search" style={styles.searchBar} onSubmit={handleSearchSubmit}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSearch();
                }
              }}
              placeholder="Search AI plugins, workflows, models, speech tools..."
              className="repository-search-input"
              style={styles.searchInput}
            />
            <button type="submit" style={styles.searchBtn}>
              Search
            </button>
          </form>
          <button className="repository-login-button" onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')} style={styles.loginBtn}>
            {isLoggedIn ? 'Account' : 'Login'}
          </button>
        </div>
      </header>

      {showingSearchResults ? (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Search Results ({searchResults.length + workflowSearchResults.length})</h3>
          {searching ? (
            <div style={styles.loading}>Searching plugins...</div>
          ) : searchResults.length > 0 || workflowSearchResults.length > 0 ? (
            <>
              {searchResults.length > 0 && <div style={styles.pluginGrid}>
                {searchResults.map((plugin) => (
                  <PluginCard key={plugin.id} plugin={plugin} onClick={() => navigate(buildPluginUrl(plugin))} />
                ))}
              </div>}
              {workflowSearchResults.length > 0 && <>
                <h3 style={styles.subsectionTitle}>Matching Workflows ({workflowSearchResults.length})</h3>
                <div style={styles.pluginGrid}>
                  {workflowSearchResults.map((workflow) => (
                    <WorkflowCard key={workflow.id} workflow={workflow} onClick={() => navigate(buildWorkflowUrl(workflow))} />
                  ))}
                </div>
              </>}
            </>
          ) : (
            <div style={styles.emptySearch}>No plugins matched "{activeSearchQuery}".</div>
          )}
        </div>
      ) : (
        <>
          <div style={styles.hero}>
            <h2 style={styles.heroTitle}>Plugins for Self-Hosted AI Chat, Local LLMs, and Agent Workflows</h2>
            <p style={styles.heroSubtitle}>
              Find full-stack GotChat extensions for custom interfaces, model management,
              speech, automation, reusable workflows, and private AI applications.
            </p>
          </div>

          <div style={styles.categories}>
            <h3 style={styles.sectionTitle}>Categories</h3>
            <div style={styles.categoryTags}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => filterByCategory(cat)}
                  style={{
                    ...styles.categoryTag,
                    ...(selectedCategory === cat ? styles.activeCategory : {})
                  }}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={styles.loading}>Loading plugins...</div>
          ) : (
            <>
              <PluginSection
                title="New Plugins"
                plugins={newPlugins}
                onPluginClick={(plugin) => navigate(buildPluginUrl(plugin))}
              />

              <PluginSection
                title="Popular Plugins"
                plugins={popularPlugins}
                onPluginClick={(plugin) => navigate(buildPluginUrl(plugin))}
              />

              <PluginSection
                title="Top Rated Plugins"
                plugins={topRatedPlugins}
                onPluginClick={(plugin) => navigate(buildPluginUrl(plugin))}
              />

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  All Plugins ({plugins.length})
                </h3>
                <div style={styles.pluginGrid}>
                  {plugins.map((plugin) => (
                    <PluginCard
                      key={plugin.id}
                      plugin={plugin}
                      onClick={() => navigate(buildPluginUrl(plugin))}
                    />
                  ))}
                </div>
              </div>

              <WorkflowSection
                title={`Workflows (${workflows.length})`}
                workflows={workflows}
                onWorkflowClick={(workflow) => navigate(buildWorkflowUrl(workflow))}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function PluginSection({ title, plugins, onPluginClick }) {
  if (plugins.length === 0) return null;

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.pluginGrid}>
        {plugins.map((plugin) => (
          <PluginCard
            key={plugin.id}
            plugin={plugin}
            onClick={() => onPluginClick(plugin)}
          />
        ))}
      </div>
    </div>
  );
}

function WorkflowSection({ title, workflows, onWorkflowClick }) {
  if (workflows.length === 0) return null;
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.pluginGrid}>
        {workflows.map((workflow) => (
          <WorkflowCard key={workflow.id} workflow={workflow} onClick={() => onWorkflowClick(workflow)} />
        ))}
      </div>
    </div>
  );
}

function toSlug(name) {
  if (!name) {
    return 'plugin';
  }
  const normalized = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || 'plugin';
}

function buildSlugCounts(items) {
  const counts = {};
  items.forEach((plugin) => {
    const slug = plugin.slug || toSlug(plugin.name);
    counts[slug] = (counts[slug] || 0) + 1;
  });
  return counts;
}

function PluginCard({ plugin, onClick }) {
  const isLoggedIn = Boolean(localStorage.getItem('token'));
  const screenshot = plugin.readmeParsed?.screenshots?.[0]?.file;
  const thumbnail = screenshot
    ? `${API_BASE}/plugin/${plugin.id}/assets?path=${encodeURIComponent(`resources/${screenshot}`)}`
    : (plugin.media?.[0]?.s3Url || 'https://via.placeholder.com/300x200');

  return (
    <div style={styles.card} onClick={onClick}>
      <div style={styles.cardImage}>
        <img src={thumbnail} alt={plugin.name} style={styles.image} />
      </div>
      <div style={styles.cardContent}>
        <div style={styles.cardTitleRow}>
          <h4 style={styles.cardTitle}>{plugin.name}</h4>
          <span style={plugin.isPaid ? styles.paidBadge : styles.freeBadge}>
            {plugin.isPaid ? (isLoggedIn ? `${plugin.currency} ${Number(plugin.price).toFixed(2)}` : 'Buy') : 'Free'}
          </span>
        </div>
        <p style={styles.cardDescription}>{plugin.readmeParsed?.summary || plugin.description}</p>

        <div style={styles.cardTags}>
          {String(plugin.tags || '').split(',').filter(Boolean).slice(0, 3).map((tag, i) => (
            <span key={i} style={styles.tag}>{tag.trim()}</span>
          ))}
        </div>

        <div style={styles.cardFooter}>
          <div style={styles.rating}>
            <span style={styles.stars}>{'★'.repeat(Math.max(0, Math.round(plugin.averageRating || 0)))}</span>
            <span style={styles.ratingText}>{Number(plugin.averageRating || 0).toFixed(1)}</span>
          </div>
          <div style={styles.downloads}>
            {Number(plugin.downloads || 0).toLocaleString()} downloads
          </div>
        </div>

        <div style={styles.author}>
          By {plugin.authorName}
        </div>
      </div>
    </div>
  );
}

function WorkflowCard({ workflow, onClick }) {
  const metadata = workflow.workflowMetadata || {};
  const assetVersion = encodeURIComponent(workflow.updatedAt || workflow.publishedAt || workflow.version || 'current');
  const thumbnail = metadata.images?.[0]
    ? `${API_BASE}/workflow/${workflow.id}/assets?path=${encodeURIComponent(metadata.images[0])}&v=${assetVersion}`
    : 'https://via.placeholder.com/300x200?text=Workflow';
  return (
    <div style={styles.card} onClick={onClick}>
      <div style={styles.cardImage}><img src={thumbnail} alt={workflow.name} style={styles.image} /></div>
      <div style={styles.cardContent}>
        <div style={styles.cardTitleRow}>
          <h4 style={styles.cardTitle}>{workflow.name}</h4>
          <span style={styles.workflowBadge}>Workflow</span>
        </div>
        <p style={styles.cardDescription}>{workflow.description}</p>
        <div style={styles.workflowCounts}>
          <span>{Number(metadata.nodeCount || 0)} nodes</span>
          <span>{Number(metadata.subflowCount || 0)} subflows</span>
        </div>
        {(metadata.skills || []).length > 0 && <div style={styles.cardTags}>
          {metadata.skills.slice(0, 3).map((skill) => <span key={skill} style={styles.tag}>{skill}</span>)}
        </div>}
        <div style={styles.cardFooter}>
          <span style={styles.downloads}>{Number(workflow.downloads || 0).toLocaleString()} downloads</span>
          <span style={styles.author}>By {workflow.authorName}</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f7fa',
    fontFamily: 'Arial, sans-serif'
  },
  cardTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '10px'
  },
  paidBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    background: '#003087',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  freeBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    background: '#e8f7ee',
    color: '#147a3e',
    fontSize: '12px',
    fontWeight: '700'
  },
  workflowBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    background: '#ede9fe',
    color: '#6d28d9',
    fontSize: '12px',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  header: {
    background: 'linear-gradient(135deg, #123647 0%, #225972 42%, #f6efe1 100%)',
    color: 'white',
    padding: '20px 0',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px'
  },
  logo: {
    fontSize: '28px',
    fontWeight: 'bold',
    margin: 0,
    letterSpacing: '-0.02em'
  },
  brandWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
    flex: '0 1 auto'
  },
  logoMark: {
    position: 'relative',
    width: '42px',
    height: '34px',
    borderRadius: '10px',
    background: '#f7f1e5',
    flex: '0 0 auto',
    boxShadow: '0 8px 18px rgba(0,0,0,0.14)'
  },
  logoMarkTail: {
    position: 'absolute',
    left: '15px',
    bottom: '-7px',
    width: '0',
    height: '0',
    borderLeft: '7px solid transparent',
    borderRight: '0 solid transparent',
    borderTop: '9px solid #f7f1e5',
  },
  logoDot: {
    position: 'absolute',
    top: '13px',
    width: '6px',
    height: '6px',
    borderRadius: '999px',
    background: '#1a1a1a'
  },
  logoDotLeft: {
    left: '9px'
  },
  logoDotCenter: {
    left: '18px'
  },
  logoDotRight: {
    left: '27px'
  },
  searchBar: {
    flex: 1,
    display: 'flex',
    maxWidth: '600px',
    gap: '10px'
  },
  searchInput: {
    flex: 1,
    padding: '12px 20px',
    border: 'none',
    borderRadius: '25px',
    fontSize: '16px',
    outline: 'none'
  },
  searchBtn: {
    padding: '12px 24px',
    background: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '25px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  loginBtn: {
    padding: '10px 24px',
    background: 'rgba(255,255,255,0.2)',
    color: 'white',
    border: '2px solid white',
    borderRadius: '20px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  hero: {
    background: 'white',
    textAlign: 'center',
    padding: '60px 20px',
    borderBottom: '1px solid #e1e8ed'
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 10px 0'
  },
  heroSubtitle: {
    fontSize: '20px',
    color: '#666',
    margin: 0
  },
  categories: {
    maxWidth: '1400px',
    margin: '40px auto',
    padding: '0 20px'
  },
  sectionTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '20px'
  },
  subsectionTitle: {
    margin: '32px 0 18px',
    fontSize: '22px',
    color: '#333'
  },
  categoryTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px'
  },
  categoryTag: {
    padding: '10px 20px',
    background: 'white',
    border: '2px solid #e1e8ed',
    borderRadius: '25px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.3s'
  },
  activeCategory: {
    background: '#667eea',
    color: 'white',
    borderColor: '#667eea'
  },
  section: {
    maxWidth: '1400px',
    margin: '40px auto',
    padding: '0 20px'
  },
  pluginGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '25px'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    cursor: 'pointer'
  },
  cardImage: {
    width: '100%',
    height: '200px',
    overflow: 'hidden',
    background: '#e1e8ed'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  cardContent: {
    padding: '20px'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 10px 0'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.5',
    margin: '0 0 15px 0',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  cardTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '15px'
  },
  workflowCounts: {
    display: 'flex',
    gap: '14px',
    marginBottom: '14px',
    color: '#5b6472',
    fontSize: '13px',
    fontWeight: '600'
  },
  tag: {
    padding: '4px 10px',
    background: '#f0f3f7',
    color: '#667eea',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '15px',
    borderTop: '1px solid #e1e8ed',
    marginBottom: '10px'
  },
  rating: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  stars: {
    fontSize: '16px'
  },
  ratingText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333'
  },
  downloads: {
    fontSize: '14px',
    color: '#666'
  },
  author: {
    fontSize: '13px',
    color: '#999',
    fontStyle: 'italic'
  },
  loading: {
    textAlign: 'center',
    padding: '100px 20px',
    fontSize: '20px',
    color: '#666'
  },
  emptySearch: {
    color: '#666',
    fontSize: '16px',
    padding: '12px 0'
  }
};

export default PluginShowcase;
